import type { Reporting } from '@local/common-lib';
import { xlsxWorkbook } from './monthly-summary-export.js';

export const yearlyPatrollerSummaryColumns: {
  key: keyof Reporting.YearlyPatrollerSummaryRow;
  label: string;
}[] = [
  { key: 'patroller', label: 'Patroller' },
  { key: 'primaryPatroller', label: 'Primary Patroller' },
  { key: 'patrollerCount', label: 'Patroller' },
  { key: 'totalPatrols', label: 'Total Patrols' },
  { key: 'duration', label: 'Time Patrolling' },
];

const yearlyPatrollerSummaryCountKeys = new Set<
  keyof Reporting.YearlyPatrollerSummaryRow
>(['patrollerCount', 'primaryPatroller', 'totalPatrols']);

export const yearlyPatrollerSummaryDisplayValue = (
  key: keyof Reporting.YearlyPatrollerSummaryRow,
  value: string | number,
): string | number =>
  yearlyPatrollerSummaryCountKeys.has(key) && value === 0 ? '-' : value;

const rowsForExport = (
  report: Reporting.YearlyPatrollerSummary,
): (string | number)[][] =>
  report.rows.map((row) =>
    yearlyPatrollerSummaryColumns.map(({ key }) => row[key]),
  );

const safeCell = (value: string | number): string => {
  const text = String(value);
  const formulaSafe = /^[=+\-@]/.test(text) ? `'${text}` : text;
  return /[",\n\r]/.test(formulaSafe)
    ? `"${formulaSafe.replaceAll('"', '""')}"`
    : formulaSafe;
};

export const yearlyPatrollerSummaryCsv = (
  report: Reporting.YearlyPatrollerSummary,
): string =>
  [
    yearlyPatrollerSummaryColumns.map(({ label }) => safeCell(label)),
    ...rowsForExport(report).map((row) => row.map(safeCell)),
  ]
    .map((row) => row.join(','))
    .join('\r\n');

export const downloadYearlyPatrollerSummaryCsv = (
  report: Reporting.YearlyPatrollerSummary,
  year: string,
): void => {
  const blob = new Blob([`\ufeff${yearlyPatrollerSummaryCsv(report)}`], {
    type: 'text/csv;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `yearly-patroller-summary-${year}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

export const downloadYearlyPatrollerSummaryXlsx = (
  report: Reporting.YearlyPatrollerSummary,
  year: string,
): void => {
  const workbook = xlsxWorkbook(
    [
      yearlyPatrollerSummaryColumns.map(({ label }) => label),
      ...rowsForExport(report),
    ],
    'Yearly Patroller Summary',
  );
  const blob = new Blob([workbook], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `yearly-patroller-summary-${year}.xlsx`;
  link.click();
  URL.revokeObjectURL(url);
};
